import { selectCurrentHotelId } from 'accessors'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import EditIcon from 'components/ReusableComponents/Icons/Edit'
import Trash from 'components/ReusableComponents/Icons/Trash'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import TextArea from 'features/ManageCampaigns/TextArea'
import { useInternalAdmin } from 'hooks'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import userApi from 'services/api/user'

import styles from './ComposeTemplates.module.scss'

const NOTE_KIND = 'compose-templates'

// Supported template tags
// Note: display must match TextArea's hardcoded transformations
// 'firstName' gets transformed to @[guestName](__guestName__)
// 'lastName' gets transformed to @[guestLastName](__guestLastName__)
const TEMPLATE_TAGS = [
  { id: '__guestName__', display: 'firstName', length: 15 },
  { id: '__guestLastName__', display: 'lastName', length: 15 },
  { id: '__hotelName__', display: 'hotelName', length: 40 }
]

const ComposeTemplates = ({ setComposeValue, guestName, hotelName }) => {
  const isInternalAdmin = useInternalAdmin()
  const rawSubsidiaryId = useSelector(selectCurrentHotelId)
  // Format subsidiaryId as string to avoid GUID casting issues
  const subsidiaryId = rawSubsidiaryId
    ? String(rawSubsidiaryId).trim().replace(/[{}]/g, '')
    : null

  const [isAddTemplateDialogVisible, setisAddTemplateDialogVisible] = useState()
  const [templates, setTemplates] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  const [activeTemplateName, setActiveTemplateName] = useState('')
  const [activeTemplateContent, setActiveTemplateContent] = useState('')
  const [originalTemplateName, setOriginalTemplateName] = useState(null)

  const noteKey = subsidiaryId ? `compose-templates-${subsidiaryId}` : null

  // Parse guestName into firstName and lastName
  const nameParts = guestName ? guestName.trim().split(/\s+/) : []
  const firstName = nameParts[0] || ''
  const lastName = nameParts.slice(1).join(' ') || ''

  // Replace template variables with actual values
  function replaceVariables(templateText) {
    if (!templateText) return ''
    return templateText
      .replace(/@\[guestName\]\(__guestName__\)/g, firstName)
      .replace(/@\[guestLastName\]\(__guestLastName__\)/g, lastName)
      .replace(/@\[hotelName\]\(__hotelName__\)/g, hotelName || '')
  }

  // Fetch templates from backend on mount
  useEffect(() => {
    async function loadTemplates() {
      if (!subsidiaryId || !noteKey) {
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        const response = await userApi.retrieveNote({
          key: noteKey,
          kind: NOTE_KIND,
          subsidiaryId
        })

        if (response?.data) {
          const parsedTemplates = JSON.parse(response.data)
          setTemplates(parsedTemplates)
        }
      } catch (error) {
        console.error('Failed to load templates:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadTemplates()
  }, [subsidiaryId, noteKey])

  // Save templates to backend
  async function saveTemplatesToBackend(newTemplates) {
    if (!subsidiaryId || !noteKey) return

    try {
      setIsSaving(true)
      await userApi.addOrUpdateNote({
        key: noteKey,
        kind: NOTE_KIND,
        data: JSON.stringify(newTemplates),
        subsidiaryId
      })
    } catch (error) {
      console.error('Failed to save templates:', error)
    } finally {
      setIsSaving(false)
    }
  }

  async function saveTemplate() {
    if (activeTemplateName && activeTemplateContent) {
      const newTemplates = { ...templates }

      // If editing and name changed, delete the old key
      if (originalTemplateName && originalTemplateName !== activeTemplateName) {
        delete newTemplates[originalTemplateName]
      }

      newTemplates[activeTemplateName] = activeTemplateContent
      setTemplates(newTemplates)
      await saveTemplatesToBackend(newTemplates)
    }
    setisAddTemplateDialogVisible(false)
    setOriginalTemplateName(null)
  }

  async function deleteTemplate(key) {
    const newTemplates = { ...templates }
    delete newTemplates[key]
    setTemplates(newTemplates)
    await saveTemplatesToBackend(newTemplates)
  }

  function editTemplate(key) {
    setOriginalTemplateName(key)
    setActiveTemplateName(key)
    setActiveTemplateContent(templates[key])
    setisAddTemplateDialogVisible(true)
  }

  function openAddTemplateDialog() {
    setOriginalTemplateName(null)
    setisAddTemplateDialogVisible(true)
    setActiveTemplateContent('')
    setActiveTemplateName('')
  }

  // Handler for TextArea changes
  function handleTemplateContentChange(text) {
    setActiveTemplateContent(text)
  }

  // Dummy handler for used tags (required by TextArea but not used here)
  function handleUpdateUsedTags() {}

  if (isLoading) {
    return (
      <div className={styles.buttons}>
        <span className={styles.loadingText}>Loading templates...</span>
      </div>
    )
  }

  return (
    <div>
      {isAddTemplateDialogVisible && (
        <ConfirmationDialog
          title={originalTemplateName ? 'Edit template' : 'Add template'}
          onClose={() => {
            setisAddTemplateDialogVisible(false)
            setOriginalTemplateName(null)
          }}
          positiveMessage={isSaving ? 'Saving...' : 'Save template'}
          onConfirm={saveTemplate}
          isButtonDisabled={isSaving}
          maxWidth={640}
          allowOverflow={true}
        >
          <div className={styles.templateForm}>
            <TextInput
              label="Template name"
              type="text"
              placeholder="Template name"
              value={activeTemplateName}
              onChange={e => setActiveTemplateName(e.target.value)}
            />
            <div className={styles.textAreaHolder}>
              <p className={styles.formLabel}>Template content</p>
              <div className={styles.textAreaWrapper}>
                <TextArea
                  handleChange={handleTemplateContentChange}
                  handleUpdateUsedTags={handleUpdateUsedTags}
                  value={activeTemplateContent}
                  baseTags={TEMPLATE_TAGS}
                  requiredTagIds={['__guestName__', '__guestLastName__', '__hotelName__']}
                  shouldShowOptOutText={false}
                  isInternalAdmin={isInternalAdmin}
                  previewPrefix=""
                  hasEmojiPicker={false}
                />
              </div>
            </div>
          </div>
        </ConfirmationDialog>
      )}
      <div className={styles.buttons}>
        <button
          className={styles.addTemplateButton}
          onClick={() => openAddTemplateDialog()}
          disabled={isSaving}
        >
          Add a template
        </button>
        {Object.keys(templates).map(key => (
          <div key={key} className={styles.templateButton}>
            <button
              className={styles.templateButtonItself}
              onClick={() => setComposeValue(replaceVariables(templates[key]))}
              disabled={isSaving}
            >
              {key}
            </button>
            <button
              className={styles.editButton}
              onClick={() => editTemplate(key)}
              disabled={isSaving}
            >
              <EditIcon />
            </button>
            <button
              className={styles.deleteButton}
              onClick={() => deleteTemplate(key)}
              disabled={isSaving}
            >
              <Trash />
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

export default ComposeTemplates
